import { Button } from '@/components/ui';

import {
  formatDays,
  inviteWaitDays,
  sentLabel,
  type PendingEntry,
} from './connection-actions';
import { RequestLine } from './request-line';
import { handOffFocus } from './request-line-focus';
import { useRunConnectionAction } from './use-run-connection-action';

import type { RequestLines } from './use-request-lines';

/** What a sent request or invite says about itself, beside the name: the address it went to, and its state */
const sentDetail = ({
  entry: { state, account, email },
}: PendingEntry): string => {
  if (state === 'requested') {
    return account?.sentAt
      ? `${email} · Requested · ${sentLabel(account.sentAt)}`
      : `${email} · Requested`;
  }
  const waitDays = account && inviteWaitDays(account);
  return waitDays
    ? `${email} · Invited · again in ${formatDays(waitDays)}`
    : `${email} · Invited`;
};

/** One request or invite that the user sent and that has no answer yet, with the one thing the user can do about it:
 * cancel a request, which closes the line, or send an invite again once the wait is over. */
export const SentRow = ({
  request,
  lines,
}: {
  request: PendingEntry;
  lines: RequestLines;
}) => {
  const { person, entry, accountId } = request;
  const { click, isPending } = useRunConnectionAction(person, 'wiki_requests');
  // A person with several sent items has a line for each, and the address tells their buttons apart
  const subject = `${person.name} (${entry.email})`;

  return (
    <RequestLine
      person={person}
      status={lines.statusOf(accountId)}
      detail={sentDetail(request)}
      onClosed={() => lines.close(accountId)}
    >
      {entry.state === 'requested' && (
        <Button
          variant="ghost"
          size="xs"
          textSize="xs"
          isLoading={isPending}
          aria-label={`Cancel request to ${subject}`}
          onClick={async (event) => {
            const handOff = handOffFocus(event.currentTarget);
            if (await lines.answer(accountId, () => click(entry), 'closing')) {
              handOff();
            }
          }}
        >
          Cancel
        </Button>
      )}
      {entry.state === 'inviteAgain' && (
        <Button
          variant="ghost"
          size="xs"
          textSize="xs"
          isLoading={isPending}
          aria-label={`Invite again ${subject}`}
          onClick={() => void click(entry)}
        >
          Invite again
        </Button>
      )}
    </RequestLine>
  );
};
